import React, { useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Collapse,
  Alert,
  makeStyles,
  Link,
  FormActions,
} from '@bw/foundry';
import { Formik, FormikConfig } from 'formik';
import {
  FormikForm,
  FormikTextField,
  FormikPasswordField,
  FormikSubmitButton,
  FormikCheckbox,
} from 'components/formikBindings';
import EditButton from 'components/EditButton';
import { getLocalWithExpiration } from 'utils/handleLocalWithExpiration';
import SignInFormAlert from 'pages/Login/SignInFormAlert';
import { useNavigate } from 'react-router-dom';
import RouteLink from '../../components/RouteLink';
import urlcat from 'urlcat';

interface FormFields {
  username: string;
  password: string;
  rememberme: boolean;
}

interface SignInFormProps {
  formikConfig: FormikConfig<FormFields>;
  errorMessage: string;
  loading: boolean;
  setErrorMessage: (error?: any, username?: string) => void;
  showPasswordField: boolean;
  breakGlass?: boolean;
}

const useStyles = makeStyles(() => ({
  message: {
    // eslint-disable-next-line
    '& .MuiAlert-message': {
      overflow: 'initial',
    },
  },
}));

const SignInForm: React.FC<SignInFormProps> = ({
  formikConfig,
  errorMessage,
  loading,
  setErrorMessage,
  showPasswordField,
  breakGlass,
}) => {
  const alertClasses = useStyles();
  const navigate = useNavigate();

  const usernameRef = useRef<HTMLInputElement | null>(null);
  const passwordRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (showPasswordField && passwordRef.current) {
      passwordRef.current.focus();
      return undefined;
    }
    // This seems to eliminate some race condition that was preventing focus from working
    // https://github.com/mui/material-ui/issues/1594
    const timeout = setTimeout(() => {
      usernameRef.current.focus();
    }, 50);
    return () => {
      clearTimeout(timeout);
    };
  }, [showPasswordField]);

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="80vh"
    >
      <Container maxWidth="sm">
        <Typography variant="h1" gutterBottom>
          Log in to Bandwidth
        </Typography>
        <Formik {...formikConfig}>
          {({ setFieldValue }) => (
            <FormikForm>
              <Box gap={2}>
                <Collapse in={!!errorMessage && !loading} unmountOnExit>
                  {!!errorMessage &&
                  getLocalWithExpiration('attempts') === 3 ? (
                    <SignInFormAlert
                      errorCode={getLocalWithExpiration('errorCode')}
                    />
                  ) : (
                    <Alert
                      severity="error"
                      sx={{
                        message: {
                          overflow: 'inherit',
                        },
                      }}
                      className={alertClasses.message}
                      onClose={() => setErrorMessage(null)}
                    >
                      {errorMessage}{' '}
                      <Link
                        href="https://support.bandwidth.com/hc/en-us/articles/1500003979401"
                        target="_blank"
                      >
                        Learn More
                      </Link>
                    </Alert>
                  )}
                </Collapse>
                <FormikTextField
                  label="Username"
                  name="username"
                  disabled={showPasswordField}
                  inputRef={usernameRef}
                  required
                  InputProps={{
                    endAdornment: showPasswordField ? (
                      <EditButton
                        onMouseDown={() => {
                          setFieldValue('password', '', false);
                          navigate(urlcat(`/login`, ''), {
                            state: {
                              username: formikConfig.initialValues.username,
                            },
                          });
                          setErrorMessage('');
                        }}
                      />
                    ) : null,
                  }}
                />
                {breakGlass && (
                  <Alert severity="warning">
                    Please visit{' '}
                    <Link href="https://accounts.bandwidth.com">
                      accounts.bandwidth.com
                    </Link>{' '}
                    to update your Single Sign-on (SSO) settings. All users on
                    your account will be unable to log in until these settings
                    are updated.
                  </Alert>
                )}
                <Collapse in={showPasswordField}>
                  <FormikPasswordField
                    inputRef={passwordRef}
                    required={showPasswordField}
                    disabled={loading}
                  />
                </Collapse>
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="space-between"
                >
                  <FormikCheckbox name="rememberme" label="Remember Me" />
                  {showPasswordField && (
                    <Link
                      component={RouteLink}
                      to="/forgot-password"
                      style={{ marginTop: 'auto', marginBottom: 'auto' }}
                    >
                      Forgot password?
                    </Link>
                  )}
                </Box>
                <FormActions>
                  <FormikSubmitButton allowWhenPristine loading={loading}>
                    {showPasswordField ? 'Sign In' : 'Next'}
                  </FormikSubmitButton>
                </FormActions>
              </Box>
            </FormikForm>
          )}
        </Formik>
      </Container>
    </Box>
  );
};
export default SignInForm;
